import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import Sidebar from '../components/ui/Sidebar';
import Header from '../components/ui/Header';
import { useSidebar } from '../contexts/SidebarContext';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/AppIcon';
import { buildApiUrl } from '../config/api';

const subscriptionPlans = [
  {
    name: 'Monthly Subscription',
    badge: 'FLEXIBLE',
    badgeColor: 'primary',
    tagline: '💡 Pay monthly, cancel anytime. Perfect for trying out all features.',
    price: '99',
    period: '/ month',
    billingCycle: 'monthly',
    cta: 'Subscribe Monthly',
    ctaStyle: 'primary',
    features: [
      { text: 'Unlimited Projection Years', available: true },
      { text: 'Full Cash-Flow Analysis', available: true },
      { text: 'Retirement Planning & Simulations', available: true },
      { text: 'Monte Carlo Projections', available: true },
      { text: 'Asset Allocation Routing', available: true },
      { text: 'Ad-Hoc & Emergency Planning', available: true },
      { text: 'Debt Prepayment Modeling', available: true },
      { text: 'Dedicated Support', available: true },
    ],
  },
  {
    name: 'Annual Subscription',
    badge: 'BEST VALUE',
    badgeColor: 'warning',
    tagline: '🚀 Save ₹189 per year! The smart choice for serious retirement planning.',
    price: '999',
    period: '/ year',
    billingCycle: 'annual',
    savings: '189',
    cta: 'Subscribe Annually',
    ctaStyle: 'primary',
    featured: true,
    features: [
      { text: 'ALL Monthly Features, PLUS:', available: true },
      { text: 'Save ₹189 annually', available: true },
      { text: 'Priority Support', available: true },
    ],
  },
];

const Upgrade = () => {
  const { user, loading: authLoading, getAuthHeaders } = useAuth();
  const { isPaid, status, refresh, loading: subscriptionLoading, isSubscribed } = useSubscription();
  const { sidebarExpanded } = useSidebar();
  const navigate = useNavigate();
  const [subscribingPlan, setSubscribingPlan] = useState(null); // 'monthly' | 'annual' when that button is loading
  const [error, setError] = useState(null);
  
  // Social proof count (can be made dynamic later)
  const paidCustomersCount = 243;

  const isRazorpayEnabled = Boolean(
    import.meta.env.VITE_RAZORPAY_KEY_ID_TEST || import.meta.env.VITE_RAZORPAY_KEY_ID_LIVE
  );

  // Redirect paid users to thank you page immediately
  useEffect(() => {
    if (!authLoading && !subscriptionLoading && isPaid) {
      navigate('/thankyou', { replace: true });
    }
  }, [authLoading, subscriptionLoading, isPaid, navigate]);

  const pollSubscriptionStatus = (maxAttempts = 15, intervalMs = 2000) => {
    let attempts = 0;
    const poll = async () => {
      try {
        await refresh();
        const res = await fetch(buildApiUrl('/auth/subscription/status'), { headers: getAuthHeaders() });
        const data = res.ok ? await res.json() : {};
        const paid = data?.plan === 'subscribed' || data?.subscriptionStatus === 'subscribed_active';
        if (paid) {
          navigate('/thankyou', { replace: true });
          setSubscribingPlan(null);
          return;
        }
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(poll, intervalMs);
        } else {
          setError('Subscription is being processed. Please refresh the page in a moment.');
          setSubscribingPlan(null);
        }
      } catch (err) {
        console.error('[Upgrade] Poll error:', err);
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(poll, intervalMs);
        } else {
          setError('Subscription is being processed. Please refresh the page in a moment.');
          setSubscribingPlan(null);
        }
      }
    };
    poll();
  };

  const handleSubscribe = async (billingCycle) => {
    setSubscribingPlan(billingCycle);
    setError(null);

    try {
      if (isRazorpayEnabled) {
        // #region agent log
        fetch('http://127.0.0.1:7760/ingest/7db84f29-4549-481d-ba7a-7529310904e6',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'855a70'},body:JSON.stringify({sessionId:'855a70',location:'upgrade.jsx:handleSubscribe',message:'subscribe_start',data:{billingCycle,isRazorpayEnabled},timestamp:Date.now(),hypothesisId:'H1'})}).catch(()=>{});
        // #endregion
        const createRes = await fetch(buildApiUrl('/payments/create-subscription'), {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ billingCycle }),
        });
        // #region agent log
        const createResOk = createRes.ok;
        const createResStatus = createRes.status;
        let createBody = null;
        try { createBody = await createRes.clone().json(); } catch (_) {}
        fetch('http://127.0.0.1:7760/ingest/7db84f29-4549-481d-ba7a-7529310904e6',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'855a70'},body:JSON.stringify({sessionId:'855a70',location:'upgrade.jsx:create-subscription_response',message:'create_subscription_response',data:{ok:createResOk,status:createResStatus,body:createBody},timestamp:Date.now(),hypothesisId:'H1'})}).catch(()=>{});
        // #endregion
        if (!createRes.ok) {
          const errData = createBody || await createRes.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to create subscription');
        }
        const { subscriptionId, mode: backendMode } = createBody || await createRes.json();
        if (!subscriptionId) throw new Error('No subscription id returned');

        const isTestMode = backendMode === 'test';
        const keyId = isTestMode
          ? import.meta.env.VITE_RAZORPAY_KEY_ID_TEST
          : import.meta.env.VITE_RAZORPAY_KEY_ID_LIVE;
        if (!keyId) {
          throw new Error(isTestMode ? 'Razorpay test key not configured (VITE_RAZORPAY_KEY_ID_TEST)' : 'Razorpay live key not configured (VITE_RAZORPAY_KEY_ID_LIVE)');
        }
        // #region agent log
        fetch('http://127.0.0.1:7760/ingest/7db84f29-4549-481d-ba7a-7529310904e6',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'855a70'},body:JSON.stringify({sessionId:'855a70',location:'upgrade.jsx:before_open',message:'before_razorpay_open',data:{subscriptionId,isTestMode,hasKeyId:!!keyId},timestamp:Date.now(),hypothesisId:'H2,H3'})}).catch(()=>{});
        // #endregion
        if (!window.Razorpay) {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.async = true;
          document.body.appendChild(script);
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
          });
        }

        const options = {
          key: keyId,
          subscription_id: subscriptionId,
          name: 'RetirementPro',
          description: `${billingCycle === 'monthly' ? 'Monthly' : 'Annual'} Subscription`,
          prefill: {
            email: user?.email || '',
            name: user?.displayName || '',
          },
          notes: {
            uid: user?.uid,
            email: user?.email,
            billing_cycle: billingCycle,
          },
          handler: () => {
            pollSubscriptionStatus();
          },
          modal: {
            ondismiss: () => setSubscribingPlan(null),
          },
        };
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        const response = await fetch(buildApiUrl('/payments/mock-subscribe'), {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ billingCycle }),
        });
        if (!response.ok) throw new Error('Subscription failed');
        await response.json();
        await refresh();
        navigate('/thankyou', { replace: true });
      }
    } catch (err) {
      // #region agent log
      fetch('http://127.0.0.1:7760/ingest/7db84f29-4549-481d-ba7a-7529310904e6',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'855a70'},body:JSON.stringify({sessionId:'855a70',location:'upgrade.jsx:catch',message:'subscribe_error',data:{message:err?.message,name:err?.name},timestamp:Date.now(),hypothesisId:'H1,H2,H4,H5'})}).catch(()=>{});
      // #endregion
      console.error('[Upgrade] Subscription error:', err);
      setError(err?.message || 'Failed to create subscription. Please try again.');
      setSubscribingPlan(null);
    } finally {
      if (!isRazorpayEnabled) setSubscribingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <Header />
      <main 
        className={`
          transition-all duration-300 ease-spring pt-16 pb-20 lg:pb-8
          ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
        `}
      >
        <div className="p-4 sm:p-6">
          {/* Dev Mode Notice - only when Razorpay keys are not configured */}
          {!isPaid && !isRazorpayEnabled && (
            <div className="max-w-[900px] mx-auto mb-6">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <p className="text-sm text-muted-foreground text-center">
                  <Icon name="AlertCircle" size={16} className="inline mr-2 text-amber-500" />
                  <strong>Development Mode:</strong> Using mock subscriptions. Razorpay integration will be enabled once API keys are configured.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="max-w-[900px] mx-auto mb-6">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <p className="text-sm text-destructive text-center">
                  <Icon name="AlertCircle" size={16} className="inline mr-2" />
                  {error}
                </p>
              </div>
            </div>
          )}
          
          {/* Show loading while checking subscription status */}
          {(authLoading || subscriptionLoading) ? (
            <div className="flex items-center justify-center min-h-screen">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : !isPaid ? (
            // Non-Paid User View with Pricing
            <div className="max-w-[900px] mx-auto">
              {/* Header Section */}
              <header className="text-center mb-12 py-5">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-foreground mb-4">
                  Unlock Unlimited Planning
                </h1>
                <span className="block text-primary font-semibold text-base sm:text-lg mt-4">
                  Join the community of <strong>{paidCustomersCount}</strong> subscribers!
                </span>
                <span className="block text-muted-foreground text-sm sm:text-base mt-2">
                  Subscription grants instant, full access. Cancel anytime.
                </span>
              </header>

              {/* Pricing Cards - items-stretch so both cards same height and Subscribe buttons align */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 lg:items-stretch mt-12">
                {subscriptionPlans.map((plan, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col rounded-2xl border text-left p-8 transition-all duration-300 min-h-[520px] ${
                      plan.featured
                        ? 'border-primary border-[3px] bg-primary/5 shadow-xl'
                        : 'border-border bg-card hover:border-primary/50 hover:shadow-md'
                    }`}
                  >
                    {/* Badge */}
                    <span
                      className={`inline-block px-4 py-1.5 rounded-full text-xs font-bold text-white mb-4 ${
                        plan.badgeColor === 'primary'
                          ? 'bg-primary'
                          : plan.badgeColor === 'warning'
                          ? 'bg-amber-500'
                          : 'bg-primary'
                      }`}
                    >
                      {plan.badge}
                    </span>

                    {/* Plan Name */}
                    <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">{plan.name}</h3>

                    {/* Tagline */}
                    <p className="text-sm sm:text-base text-muted-foreground mb-6 leading-relaxed min-h-[40px]">
                      {plan.tagline}
                    </p>

                    {/* Price */}
                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-muted-foreground mr-1">₹</span>
                        <span className="text-5xl font-extrabold text-foreground tabular-nums">{plan.price}</span>
                        <span className="text-xl text-muted-foreground font-medium ml-1">{plan.period}</span>
                      </div>
                      {plan.savings && (
                        <p className="text-sm text-success font-semibold mt-2">
                          Save ₹{plan.savings} compared to monthly
                        </p>
                      )}
                    </div>

                    {/* Features List - all items bold; flex-1 so button aligns across cards */}
                    <ul className="flex-1 mt-4 min-h-0">
                      {plan.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className={`flex items-start gap-4 py-3 border-b border-border text-sm sm:text-base font-semibold ${
                            feature.available ? 'text-foreground' : 'text-muted-foreground/40'
                          }`}
                        >
                          {feature.available ? (
                            <span className="text-success font-bold flex-shrink-0 mt-0.5">✓</span>
                          ) : (
                            <span className="text-border font-bold flex-shrink-0 mt-0.5">—</span>
                          )}
                          <span>{feature.text}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button - mt-auto aligns button to bottom so both cards have buttons at same level */}
                    <button
                      onClick={() => handleSubscribe(plan.billingCycle)}
                      disabled={subscribingPlan !== null}
                      className={`mt-auto pt-8 w-full py-4 px-6 rounded-lg font-bold text-xl transition-all duration-200 flex items-center justify-center ${
                        plan.featured
                          ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl'
                          : 'bg-primary text-primary-foreground hover:bg-primary/90'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {subscribingPlan === plan.billingCycle ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Processing...
                        </span>
                      ) : (
                        plan.cta
                      )}
                    </button>
                  </div>
                ))}
              </div>

              {/* Free Tier Info */}
              <div className="bg-muted/50 border border-border rounded-xl p-6 mb-12">
                <h3 className="text-lg font-bold text-foreground mb-3">
                  <Icon name="Info" size={20} className="inline mr-2 text-primary" />
                  Free Tier Limitations
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    <span>Limited to 5-year projections only</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    <span>Charts and data beyond 5 years are obscured</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    <span>Upgrade to unlock unlimited projection years</span>
                  </li>
                </ul>
              </div>

              {/* Footer Disclaimer */}
              <p className="text-sm text-muted-foreground text-center mt-10">
                *All prices are in Indian Rupees (₹). Cancel anytime from your billing page.*
              </p>
            </div>
          ) : (
            // Paid users should be redirected, but show loading just in case
            <div className="flex items-center justify-center min-h-screen">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Upgrade;
